/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-useless-escape */
import { DEFAULT_TENANT } from '../config/config';
import translation from '../config/locales.json';
import tenantsConfig from '../config/tenantsConfig.json';
import { IFootballMatchEvent } from '../interfaces/interfaces';

type IstringIndexTenants = {
  [key: string]: { name: string; language: string };
};
type IstringIndexLocale = {
  [key: string]: { [key: string]: string };
};

function getParameterByName(optionName: string, url = window.location.href) {
  // eslint-disable-next-line no-param-reassign
  const name = optionName.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return '';
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export const getTenantFromHost = (): string => {
  const host = document.referrer;

  const tenant = Object.keys(tenantsConfig).find((item) => host.includes(item));

  return tenant || DEFAULT_TENANT;
};

export const getLanguageOption = (): string => {
  const queryStrings = window.location.search;
  if (queryStrings.indexOf('language') === -1) {
    const tenant = getTenantFromHost();
    if (tenant === '20min') {
      const host = document.referrer;
      if (host.includes('/fr')) {
        return 'fr';
      }
      return 'de';
    }
    return (<IstringIndexTenants>tenantsConfig)[tenant].language;
  }
  return getParameterByName('language');
};

export const getMatchIdOption = (): string => {
  return getParameterByName('matchId');
};

export const getSportTypeOption = (): string => {
  const allowedTypes = ['football', 'icehockey'];
  const sportType = getParameterByName('sportType');
  return allowedTypes.find((type) => type === String(sportType).toLowerCase()) || 'football';
};

export const trans = (string: string): string => {
  let lang = getLanguageOption();
  if (!(<IstringIndexLocale>translation)[lang]) {
    lang = 'de';
  }
  if (!(<IstringIndexLocale>translation)[lang][string]) return string;
  return (<IstringIndexLocale>translation)[lang][string];
};

// Determines the current match stage (first-half, injury time, second half...)
export const matchStage = (
  matchEvents: IFootballMatchEvent[],
  sportType: string,
  postponed: boolean
): string => {
  if (postponed) return 'postponed';
  if (!matchEvents) return '';

  const eventPrecedenceOrderBySportType: { [key: string]: string[] } = {
    football: [
      'game-start',
      // 'first-half-additional-time',
      'first-half-end',
      'second-half-start',
      // 'second-half-additional-time',
      'second-half-end',
      'first-extra-start',
      // 'first-extra-additional-time',
      'first-extra-end',
      'second-extra-start',
      // 'second-extra-additional-time',
      'second-extra-end',
      'penalty-start',
      'game-end'
    ],
    icehockey: [
      'game-start',
      'first-third-end',
      'second-third-start',
      'second-third-end',
      'third-third-start',
      'third-third-end',
      'overtime-start',
      'overtime-end',
      'penalty-start',
      'game-end'
    ]
  };

  const sportFirstStage: { [key: string]: string } = {
    football: 'first-half',
    icehockey: 'first-third'
  };

  const precedenceOrder: string[] = eventPrecedenceOrderBySportType[sportType];

  // Find the current game stage in the event array
  const stage = precedenceOrder.reduce((acc, curr) => {
    return (
      matchEvents
        .map(({ matchAction }) => matchAction)
        .filter((event) => {
          return precedenceOrder.includes(event);
        })
        .find((event) => event === curr) || acc
    );
  }, '');

  return stage !== 'game-start' ? stage : sportFirstStage[sportType];
};

export const getUTCGameTime = (gameDate: string): number => {
  if (!gameDate) return 0;
  const editedDate = gameDate.slice(0, -1);
  const gameTimeInUtc = new Date(editedDate).toISOString();
  return new Date(gameTimeInUtc).getTime();
};

export const getUTCFromCET = (): number => {
  const cetTime = new Date().toLocaleString('en-GB', { timeZone: 'Europe/Zurich' });
  // cetTime will be in this format: 04/03/2021, 11:36:17
  const transformedDateTime = convertLocaleFormatToZulu(cetTime);
  const gameTimeInUtc = new Date(transformedDateTime).toISOString();
  // gameTimeInUtc will be the same cetTime date converted to this format: '2021-03-02T21:10:00.000Z'
  return new Date(gameTimeInUtc).getTime();
};

export const gameStartsAt = (gameDate: string): string => {
  if (!gameDate) return '';
  // Get current utc timestamp
  const currentUTCTime = Date.now();
  // Pretend that current time in zurich is current utc time
  const utcTimePretendCet = `${convertLocaleFormatToZulu(
    new Date().toLocaleString('en-GB', { timeZone: 'Europe/Zurich' })
  )}Z`;
  // Pretend that current zurich time is current utc time and get timestamp as if it's the same hour in utc as it is currently in zurich
  // if it's 21h in zurich pretend it's also 21h in utc
  const pretendCetTimestamp = new Date(utcTimePretendCet).getTime();
  const zurichTimeOffset = pretendCetTimestamp - currentUTCTime;
  // Game date is already behaving like zurich time is actually utc time, get the timestamp as if the game is in utc time
  const gameDatePretendIsUtc = new Date(gameDate).getTime();
  // Now we have - if utc time is now 20h, gameDatePretendIsUtc: contains timestamp when utc clock gets to eg 21h (game time in zurich),
  // and through zurichTimeOffset we have timezone offset between cet and utc
  // By subtracting the offset from the utc timestamp (gameDatePretendIsUtc) that is set in the future, we get true utc timestamp of the game
  const utcTimeStampOfGame = gameDatePretendIsUtc - zurichTimeOffset;
  const localGameTime = new Date(utcTimeStampOfGame);
  const [localHour, localMinute] = localGameTime.toLocaleTimeString('en-GB').split(':');
  const localTime = [localHour, localMinute].join(':');
  return localTime;
};

function convertLocaleFormatToZulu(localeTime: string) {
  // cetTime will be in this format: 04/03/2021, 11:36:17
  const [date, hours] = localeTime.split(',');
  const [day, month, year] = date.split('/');
  const transformedDate = `${year}-${month}-${day}`;

  const time = `T${hours.trim()}.000`;
  const transformedDateTime = `${transformedDate}${time}`;
  // const gameTimeInUtc = new Date(transformedDateTime).toISOString();
  // gameTimeInUtc will be the same cetTime date converted to this format: '2021-03-02T21:10:00.000Z'
  return transformedDateTime;
}
